import React, { useState, useRef, useEffect } from 'react';
import { Delete, Search, Folder, Star, StarBorder } from '@mui/icons-material';
import { useChatContext } from '../context/ChatContext';

interface PromptVaultDropdownProps {
  open: boolean;
  onClose: () => void;
  onSelectPrompt: (content: string) => void;
  position?: { top?: number; bottom?: number; left: number };
}

const PromptVaultDropdown: React.FC<PromptVaultDropdownProps> = ({
  open,
  onClose,
  onSelectPrompt,
  position
}) => {
  const { appState, deletePromptFromVault, searchPromptVault, updatePromptUsage, togglePromptStar } = useChatContext();
  const [searchQuery, setSearchQuery] = useState('');
  const [deletingPrompt, setDeletingPrompt] = useState<string | null>(null);
  const [starringPrompt, setStarringPrompt] = useState<string | null>(null);
  const [searchResults, setSearchResults] = useState<{ name: string; content: string; isStarred?: boolean }[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showingSearchResults, setShowingSearchResults] = useState(false);
  const [hoveredPrompt, setHoveredPrompt] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Setup dropdown when it opens
  useEffect(() => {
    if (open) {
      setSearchQuery('');
      // Focus search input
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [open]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [open, onClose]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [open, onClose]);

  // Handle search on Enter key press
  const handleSearch = async () => {
    if (searchQuery.trim() === '') {
      // User pressed Enter with empty search - revert to default view
      setShowingSearchResults(false);
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const results = await searchPromptVault(searchQuery.trim());
      setSearchResults(results);
      setShowingSearchResults(true);
    } catch (error) {
      console.error('Error searching prompts:', error);
      setSearchResults([]);
      setShowingSearchResults(false);
    } finally {
      setIsSearching(false);
    }
  };

  // Handle search input key events
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  // Reset search when dropdown closes
  useEffect(() => {
    if (!open) {
      setSearchQuery('');
      setSearchResults([]);
      setShowingSearchResults(false);
      setIsSearching(false);
      setHoveredPrompt(null);
    }
  }, [open]);

  // Sort prompts: starred first, then by name
  const sortPrompts = (prompts: { name: string; content: string; isStarred?: boolean }[]) => {
    return [...prompts].sort((a, b) => {
      // First, sort by starred status (starred items first)
      if (a.isStarred && !b.isStarred) return -1;
      if (!a.isStarred && b.isStarred) return 1;
      
      // Then sort by name alphabetically
      return a.name.localeCompare(b.name);
    });
  };

  // Determine which prompts to show
  const displayedPrompts = sortPrompts(showingSearchResults ? searchResults : appState.promptVault);

  const handleSelectPrompt = async (content: string, name: string) => {
    try {
      // Track usage asynchronously (don't wait for it)
      updatePromptUsage(name).catch(error => {
        console.error('Failed to update usage:', error);
      });
      
      onSelectPrompt(content);
      onClose();
    } catch (error) {
      console.error('Error selecting prompt:', error);
      onSelectPrompt(content);
      onClose();
    }
  };

  const handleDeletePrompt = async (e: React.MouseEvent, promptName: string) => {
    e.stopPropagation();
    
    if (deletingPrompt === promptName) return;
    
    setDeletingPrompt(promptName);
    try {
      await deletePromptFromVault(promptName);
    } catch (error) {
      console.error('Error deleting prompt:', error);
    } finally {
      setDeletingPrompt(null);
    }
  };

  const handleToggleStar = async (e: React.MouseEvent, promptName: string) => {
    e.stopPropagation();
    
    if (starringPrompt === promptName) return;
    
    setStarringPrompt(promptName);
    try {
      await togglePromptStar(promptName);
    } catch (error) {
      console.error('Error toggling star:', error);
    } finally {
      setStarringPrompt(null);
    }
  };

  if (!open) return null;

  const dropdownStyle: React.CSSProperties = {
    position: 'fixed',
    width: '320px',
    height: '400px',
    backgroundColor: 'white',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    zIndex: 3000,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    ...(position && {
      ...(position.top !== undefined && { top: position.top }),
      ...(position.bottom !== undefined && { bottom: position.bottom }),
      left: position.left,
    })
  };

  return (
    <div ref={dropdownRef} style={dropdownStyle}>
      {/* Header with search */}
      <div style={{
        padding: '12px',
        borderBottom: '1px solid #f0f0f0',
        backgroundColor: '#fafafa'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '8px'
        }}>
          <Folder sx={{ fontSize: 16, color: '#666' }} />
          <span style={{
            fontSize: '14px',
            fontWeight: 600,
            color: '#333'
          }}>
            PromptVault
          </span>
        </div>
        
        {/* Search input */}
        <div style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center'
        }}>
          <Search sx={{
            position: 'absolute',
            left: '8px',
            fontSize: 16,
            color: '#999'
          }} />
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            placeholder="Search prompts (press Enter)..."
            style={{
              width: '100%',
              padding: '6px 12px 6px 32px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '12px',
              outline: 'none',
              transition: 'border-color 0.2s',
              boxSizing: 'border-box'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = 'var(--primary-color, #007bff)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#ddd';
            }}
          />
        </div>
      </div>

      {/* Prompts list */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        minHeight: '0'
      }}>
        {appState.isLoadingPromptVault || isSearching ? (
          <div style={{
            padding: '20px',
            textAlign: 'center',
            color: '#666',
            fontSize: '14px'
          }}>
            {isSearching ? 'Searching...' : 'Loading prompts...'}
          </div>
        ) : displayedPrompts.length === 0 ? (
          <div style={{
            padding: '20px',
            textAlign: 'center',
            color: '#666',
            fontSize: '14px'
          }}>
            {searchQuery ? 'No prompts found matching your search.' : 'No prompts found.'}
            <div style={{
              fontSize: '12px',
              marginTop: '4px',
              color: '#999'
            }}>
              {!searchQuery && 'Right-click on a user message (your prompt) to save your first prompt.'}
            </div>
          </div>
        ) : (
          displayedPrompts.map((prompt, index) => (
            <div
              key={prompt.name}
              style={{
                padding: '12px',
                borderBottom: index < displayedPrompts.length - 1 ? '1px solid #f0f0f0' : 'none',
                cursor: 'pointer',
                transition: 'background-color 0.15s',
                position: 'relative',
                minHeight: '60px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                backgroundColor: hoveredPrompt === prompt.name ? '#f8f9fa' : 'transparent'
              }}
              onClick={() => handleSelectPrompt(prompt.content, prompt.name)}
              onMouseEnter={() => setHoveredPrompt(prompt.name)}
              onMouseLeave={() => setHoveredPrompt(null)}
            >
              {/* Prompt name */}
              <div style={{
                fontSize: '14px',
                fontWeight: 500,
                color: '#333',
                marginBottom: '4px',
                paddingRight: '40px', // Space for icon column
                wordBreak: 'break-word',
                lineHeight: '1.3'
              }}>
                {prompt.name}
              </div>
              
              {/* Prompt preview */}
              <div style={{
                fontSize: '12px',
                color: '#666',
                paddingRight: '40px', // Space for icon column
                lineHeight: '1.3',
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical'
              }}>
                {prompt.content}
              </div>

              {/* Star button - show always if starred, only on hover if not starred */}
              {(prompt.isStarred || hoveredPrompt === prompt.name) && (
                <button
                  onClick={(e) => handleToggleStar(e, prompt.name)}
                  disabled={starringPrompt === prompt.name}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    right: '8px',
                    transform: 'translateY(calc(-50% - 16px))',
                    width: '24px',
                    height: '24px',
                    border: 'none',
                    borderRadius: '4px',
                    backgroundColor: 'transparent',
                    color: prompt.isStarred ? '#ffc107' : '#666',
                    cursor: starringPrompt === prompt.name ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.15s',
                    opacity: starringPrompt === prompt.name ? 0.5 : 1
                  }}
                  title={prompt.isStarred ? 'Remove from favorites' : 'Add to favorites'}
                  onMouseEnter={(e) => {
                    if (starringPrompt !== prompt.name) {
                      (e.target as HTMLElement).style.color = '#ffc107';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (starringPrompt !== prompt.name) {
                      (e.target as HTMLElement).style.color = prompt.isStarred ? '#ffc107' : '#666';
                    }
                  }}
                >
                  {prompt.isStarred ? <Star sx={{ fontSize: 16 }} /> : <StarBorder sx={{ fontSize: 16 }} />}
                </button>
              )}

                              {/* Delete button - only show on hover, positioned below star */}
                {hoveredPrompt === prompt.name && (
                  <button
                    onClick={(e) => handleDeletePrompt(e, prompt.name)}
                    disabled={deletingPrompt === prompt.name}
                    style={{
                      position: 'absolute',
                      top: '50%',
                      right: '8px',
                      transform: 'translateY(calc(-50% + 16px))',
                      width: '24px',
                      height: '24px',
                      border: 'none',
                      borderRadius: '4px',
                      backgroundColor: 'transparent',
                      color: '#666',
                      cursor: deletingPrompt === prompt.name ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.15s',
                      opacity: deletingPrompt === prompt.name ? 0.5 : 1
                    }}
                    title="Delete prompt"
                    onMouseEnter={(e) => {
                      if (deletingPrompt !== prompt.name) {
                        (e.target as HTMLElement).style.backgroundColor = '#ffebee';
                        (e.target as HTMLElement).style.color = '#d32f2f';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (deletingPrompt !== prompt.name) {
                        (e.target as HTMLElement).style.backgroundColor = 'transparent';
                        (e.target as HTMLElement).style.color = '#666';
                      }
                    }}
                  >
                    <Delete sx={{ fontSize: 14 }} />
                  </button>
                )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default PromptVaultDropdown; 